import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GeofenceScheduleFields } from "@/components/dialogs/GeofenceScheduleFields";
import { geofenceService } from "@/services/api";
import { toast } from "sonner";

const EMPTY = {
  name: "", radius: "", centerLat: "", centerLng: "",
  alertOnEnter: true, alertOnExit: true, activeDays: null, activeStartTime: "", activeEndTime: "",
};

export function EditGeofenceDialog({ open, onOpenChange, geofence, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (!open || !geofence) return;
    setForm({
      name: geofence.name || "",
      radius: geofence.radius ?? "",
      centerLat: geofence.centerLat ?? "",
      centerLng: geofence.centerLng ?? "",
      alertOnEnter: geofence.alertOnEnter ?? true,
      alertOnExit: geofence.alertOnExit ?? true,
      activeDays: geofence.activeDays ?? null,
      activeStartTime: geofence.activeStartTime || "",
      activeEndTime: geofence.activeEndTime || "",
    });
  }, [open, geofence]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const patch = {
        name: form.name,
        alertOnEnter: form.alertOnEnter,
        alertOnExit: form.alertOnExit,
        activeDays: form.activeDays,
        activeStartTime: form.activeStartTime,
        activeEndTime: form.activeEndTime,
      };
      // Polygon boundaries aren't editable here — there's no draw-on-map
      // editor yet, only the circle fields below apply to that shape.
      if (geofence.type === "circle") {
        if (form.radius !== "") patch.radius = form.radius;
        if (form.centerLat !== "") patch.centerLat = form.centerLat;
        if (form.centerLng !== "") patch.centerLng = form.centerLng;
      }
      await geofenceService.update(geofence.id, patch);
      toast.success("Geofence updated");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not update geofence");
    } finally {
      setSaving(false);
    }
  };

  if (!geofence) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="edit-geofence-dialog">
        <DialogHeader>
          <DialogTitle>Edit Geofence</DialogTitle>
          <DialogDescription>
            {geofence.type === "circle"
              ? "Update the boundary, alerts and active schedule."
              : "Polygon boundaries can't be redrawn here yet — name, alerts and schedule can still be changed."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
          <div className="space-y-1.5"><Label>Name</Label><Input required value={form.name} onChange={set("name")} data-testid="edit-geofence-name-input" /></div>
          {geofence.type === "circle" && (
            <>
              <div className="space-y-1.5"><Label>Radius (meters)</Label><Input type="number" min="1" value={form.radius} onChange={set("radius")} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Center latitude</Label><Input type="number" step="any" value={form.centerLat} onChange={set("centerLat")} /></div>
                <div className="space-y-1.5"><Label>Center longitude</Label><Input type="number" step="any" value={form.centerLng} onChange={set("centerLng")} /></div>
              </div>
            </>
          )}
          <GeofenceScheduleFields form={form} setForm={setForm} />
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="edit-geofence-submit">{saving ? "Saving…" : "Save Changes"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
